import { API } from 'src/utils/interceptor'

/**
 * Dashboard "Orders Awaiting Approval" API helpers.
 * All endpoints are authenticated (Cognito) and venue-scoped server-side;
 * the signed-in manager additionally needs the orderApprovals capability.
 */

const EMPTY_LIST = { items: [], count: 0, total: 0 }

/** Pending approval batches for the venue. `count` excludes expired batches. */
export async function fetchPendingApprovals({ venueId, apiGet = API.get }) {
  if (!venueId) return EMPTY_LIST

  // API.get resolves to the response body directly (see src/utils/interceptor)
  const data = await apiGet('api2', `venue-approvals/${venueId}/pending`)

  if (!data?.success) {
    throw new Error(data?.message || 'Failed to load orders awaiting approval.')
  }

  return {
    items: Array.isArray(data.items) ? data.items : [],
    count: Number(data.count) || 0,
    total: Number(data.total) || 0,
  }
}

/** Badge count only — failures stay silent so the nav never blocks the app. */
export async function fetchPendingApprovalsCount({
  venueId,
  apiGet = API.get,
}) {
  if (!venueId) return 0
  try {
    const list = await fetchPendingApprovals({ venueId, apiGet })
    return list.count
  } catch (error) {
    console.error('fetchPendingApprovalsCount failed', error)
    return 0
  }
}

/** Full approval detail for one batch (same payload shape as the email link). */
export async function fetchApprovalDetail({
  venueId,
  token,
  apiGet = API.get,
}) {
  if (!venueId || !token) {
    throw new Error('venueId and token are required.')
  }

  // API.get resolves to the response body directly (see src/utils/interceptor)
  const data = await apiGet('api2', `venue-approvals/${venueId}/${token}`)

  if (!data?.success) {
    throw new Error(data?.message || 'Failed to load approval details.')
  }

  return data.data
}

/**
 * Approve ('approve') or reject ('decline') a batch from the dashboard.
 * Uses the same approval workflow as emailed links; the server records the
 * signed-in manager plus the `dashboard` decision method for audit.
 */
export async function submitApprovalDecision({
  venueId,
  token,
  action,
  message = '',
  items = null,
  apiPost = API.post,
}) {
  if (!venueId || !token) {
    throw new Error('venueId and token are required.')
  }
  if (action !== 'approve' && action !== 'decline') {
    throw new Error('action must be "approve" or "decline".')
  }

  const body = { action, message }
  if (action === 'approve' && Array.isArray(items)) {
    body.items = items.map(item => ({
      orderId: item.orderId,
      quantity: item.quantity,
    }))
  }

  // API.post resolves to the response body directly (see src/utils/interceptor)
  const data = await apiPost(
    'api2',
    `venue-approvals/${venueId}/${token}/${action}`,
    { body },
  )

  if (!data?.success) {
    const error = new Error(data?.message || `Failed to ${action} the order.`)
    error.code = data?.code
    throw error
  }

  return data
}

/** Oldest-first copy-friendly formatting for submitted / expiry times. */
export function formatApprovalTimestamp(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export async function fetchApprovalSetup({ venueId, apiGet = API.get }) {
  const data = await apiGet('api2', `venue-approvals/${venueId}/setup`)
  if (!data?.success || typeof data.configured !== 'boolean') {
    throw new Error(data?.message || 'Failed to load approval setup.')
  }
  return {
    configured: data.configured,
    canConfigure: data.canConfigure === true,
  }
}

export function formatApprovalAge(value, now = Date.now()) {
  if (!value) return 'Waiting time unavailable'
  const timestamp = new Date(value).getTime()
  if (!Number.isFinite(timestamp)) return 'Waiting time unavailable'
  const minutes = Math.max(0, Math.floor((now - timestamp) / 60000))
  if (minutes < 1) return 'Just submitted'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  return `Waiting ${days} day${days === 1 ? '' : 's'}`
}

export function filterApprovalItems(items, query) {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  return items.filter(item => {
    const text = [
      item.supplierTitle,
      item.requestedBy,
      item.purchaseOrderNumber,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    return terms.every(term => text.includes(term))
  })
}
